import { UserModel } from './interfaces';

export const getUserInitials = (data: UserModel): string =>
  `${data?.firstName?.charAt(0) ?? ''}${data?.lastName?.charAt(0) ?? ''}`.toUpperCase();

export const getUserInitialsFromUsername = (username: string): JSX.Element => {
  const [firstName, lastName] = username.split(' ');

  return (
    <div className="rounded-full bg-gray-400 text-white w-6 h-6 flex items-center justify-center">
      {`${firstName?.charAt(0).toUpperCase() ?? ''} ${lastName?.charAt(0).toUpperCase() ?? ''}`}
    </div>
  );
};

export const highlightText = (text: string, query: string) => {
  if (!query) return text;

  const parts = (text || '').split(new RegExp(`(${query})`, 'gi'));

  return parts.map((part, index) => {
    if (typeof part === 'string' && part.toLowerCase() === query.toLowerCase()) {
      return (
        <span key={index} className="text-gray-900 font-medium">
          {part}
        </span>
      );
    }
    return part;
  });
};

export const getFullName = (
  firstName: string | null | undefined = '',
  lastName: string | null | undefined = ''
): string => {
  if (!firstName && !lastName) {
    return '-';
  }

  return `${firstName ?? ''} ${lastName ?? ''}`;
};

export const generateRandomEmail = () => {
  const randomString = Math.random().toString(36).substring(7);
  return `${randomString}@example.com`;
};

export const isErrorWithStatus = (
  error: unknown
): error is { originalStatus: number; message: string } => {
  return (
    typeof error === 'object' && error !== null && 'originalStatus' in error && 'message' in error
  );
};

export interface SelectOption {
  value: string;
  label: string;
}

export const removeWhiteSpaces = (currentValue: string) => {
  const sanitizedValue = currentValue.replace(/\s+/g, '');
  return sanitizedValue;
};

export const getInitialsFromCookies = (fullName: string) => {
  const initials = fullName
    ?.split(' ')
    .map((name) => name[0])
    .join('')
    .toUpperCase();

  return (
    <div className="flex items-center justify-center w-8 h-8 bg-blue-500 text-white font-semibold rounded-full">
      {initials}
    </div>
  );
};

export const isLocalhost = () => {
  const hostname = window.location.hostname;

  return (
    hostname.includes('localhost') ||
    hostname === '[::1]' ||
    hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/) ||
    hostname.endsWith('.localhost')
  );
};

export const actionTypeOptions = [
  { label: 'All', value: 'all' },
  { label: 'Created', value: 'Provider' },
  { label: 'Error', value: 'Error' },
  { label: 'Expired', value: 'Expired' },
  { label: 'Verified', value: 'Verified' },
  { label: 'Initiated', value: 'Initial' },
  { label: 'Submitted', value: 'Submitted' },
  { label: 'GreenID', value: 'system' },
  { label: 'Manually Approved', value: 'ManuallyApproved' },
  { label: 'Manually Rejected', value: 'ManuallyRejected' },
];
export const parseAddress = (addressString: string) => {
  if (!addressString || typeof addressString !== 'string') {
    return {};
  }

  const parts = addressString.split(',').map((part) => part.trim());

  const streetParts = parts[0]?.match(/^(\d+)\s(.+)$/);
  const fullStreetName = streetParts ? streetParts[2] : '';

  const streetNameParts = fullStreetName.split(' ');
  const streetType = streetNameParts.pop() || '';
  const streetName = streetNameParts.join(' ');

  const statePostcodeMatch = parts[2]?.match(/([A-Z]+)\s(\d+)/);

  return {
    streetNumber: streetParts ? streetParts[1] : '',
    streetName: streetName,
    streetType: streetType,
    suburb: parts[1] || '',
    state: statePostcodeMatch ? statePostcodeMatch[1] : '',
    postcode: statePostcodeMatch ? statePostcodeMatch[2] : '',
    country: parts[3] || '',
  };
};

export const validateACN = (data?: string) => {
  try {
    if (!data) return false;

    const temp = data.replace('-', '').replaceAll(' ', '');
    let isValid = false;
    let nTotal = 0;
    let nMod = 0;
    let checkDigit = 0;

    if (temp.length !== 9) {
      return false;
    }

    if (temp === '000000000') {
      isValid = true;
    }

    for (let i = 1; i <= 8; i++) {
      let intTemp = Number(temp.substring(i - 1, i));
      intTemp = intTemp * (9 - i);
      nTotal = nTotal + intTemp;
    }

    nMod = nTotal % 10;

    if (nMod === 0) {
      nMod = 10;
    }

    checkDigit = 10 - nMod;

    isValid = `${checkDigit}` === temp[8];

    return isValid;
  } catch {
    return false;
  }
};

export function extractStringParts(str: string) {
  const words = str.trim().split(/\s+/);

  if (words.length === 0) return ['', '', ''];
  if (words.length === 1) return [words[0], '', ''];
  if (words.length === 2) return [words[0], '', words[1]];

  const first = words[0];
  const last = words[words.length - 1];
  const middle = words.slice(1, -1).join(' ');

  return [first, middle, last];
}
